import { useEffect, useState } from 'react'
import { Button, DatePicker, Dialog, EmptyNote, Field, IconButton, Icons, Loading, StateNote, StatusLabel, Truncate } from '@workflow/ui'
import { navigate } from '@/app/router'
import { describeError } from '@/lib/api/client'
import { useCreatePaymentSheet, usePaymentSheets } from '@/lib/api/hooks'
import { PageFrame } from '@/shell/PageFrame'
import { hoursText, periodLabel } from './sheetMath'

const pad = (value: number) => String(value).padStart(2, '0')
const thisMonth = () => {
  const date = new Date()
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}`
}
const monthRange = (month: string): [string, string] => {
  const [year, value] = month.split('-').map(Number) as [number, number]
  return [`${month}-01`, `${month}-${pad(new Date(year, value, 0).getDate())}`]
}

/** Payments: the studio's monthly sheets, newest first, and where to start the next one. */
export function PaymentSheetsPage() {
  const sheets = usePaymentSheets()
  const [making, setMaking] = useState(false)
  return (
    <PageFrame
      title="Payments"
      description="One sheet per month, built from the hours players log. Mark each player paid as the money goes out."
      actions={<Button size="sm" variant="primary" icon={<Icons.plus />} onClick={() => setMaking(true)}>New sheet</Button>}
    >
      {sheets.isLoading ? (
        <Loading label="Loading payment sheets" />
      ) : sheets.error || !sheets.data ? (
        <StateNote title="The sheets did not load." detail={describeError(sheets.error)} />
      ) : sheets.data.length === 0 ? (
        <EmptyNote title="No payment sheets yet." detail="Start one for a month; every player's unpaid hours up to its last day are gathered into it." />
      ) : (
        <ul className="overflow-hidden rounded-2xl border border-hairline bg-surface">
          {sheets.data.map((sheet) => (
            <li key={sheet.id} className="border-b border-hairline last:border-0">
              <button
                type="button"
                onClick={() => navigate({ name: 'payment-sheet', id: sheet.id })}
                className="flex w-full flex-col items-start gap-2 px-4 py-3.5 text-left transition-colors duration-fast hover:bg-tint sm:flex-row sm:items-center sm:gap-4 sm:px-5"
              >
                <span className="w-full min-w-0 flex-1">
                  <Truncate text={sheet.title} className="text-ui font-medium text-foreground" />
                  <span className="mt-0.5 block text-caption text-muted-foreground">
                    {periodLabel(sheet.periodStart, sheet.periodEnd)} · {sheet.players} {sheet.players === 1 ? 'player' : 'players'} · {hoursText(sheet.hours)}h
                  </span>
                </span>
                {/* On a phone the standing drops under the title instead of squeezing it. */}
                <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
                  {sheet.disputed > 0 ? <StatusLabel tone="attention" label={`${sheet.disputed} disputed`} /> : null}
                  <span className="text-caption text-muted-foreground tabular-nums">{sheet.paid} of {sheet.players} paid</span>
                  <StatusLabel tone={sheet.sharedAt ? 'done' : 'waiting'} label={sheet.sharedAt ? 'Shared' : 'Not shared'} />
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <NewSheet open={making} onClose={() => setMaking(false)} />
    </PageFrame>
  )
}

/** A month by default; any two dates when the studio pays on another rhythm. */
function NewSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [month, setMonth] = useState(thisMonth)
  const [custom, setCustom] = useState(false)
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')
  const create = useCreatePaymentSheet((id) => {
    onClose()
    navigate({ name: 'payment-sheet', id })
  })
  useEffect(() => {
    if (!open) return
    setMonth(thisMonth())
    setCustom(false)
    create.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])
  useEffect(() => {
    const [first, last] = monthRange(month)
    setStart(first)
    setEnd(last)
  }, [month])
  const [periodStart, periodEnd] = custom ? [start, end] : monthRange(month)
  const ready = Boolean(periodStart && periodEnd && periodStart <= periodEnd)

  return (
    <Dialog open={open} onClose={onClose} labelledBy="new-sheet-label" className="sm:max-w-md">
      <form
        className="rounded-3xl border bg-surface p-6 shadow-modal"
        onSubmit={(event) => {
          event.preventDefault()
          if (ready) create.mutate({ periodStart, periodEnd })
        }}
      >
        <div className="mb-4 flex items-center justify-between">
          <p id="new-sheet-label" className="text-ui font-medium text-foreground">New payment sheet</p>
          <IconButton label="Close" size="sm" onClick={onClose} tooltipSide="bottom"><Icons.close /></IconButton>
        </div>
        {custom ? (
          <div className="grid grid-cols-2 gap-3">
            <Field label="From" htmlFor="sheet-start"><DatePicker id="sheet-start" value={start} onChange={setStart} /></Field>
            <Field label="To" htmlFor="sheet-end"><DatePicker id="sheet-end" value={end} onChange={setEnd} invalid={Boolean(start && end && start > end)} /></Field>
          </div>
        ) : (
          <Field label="Month" htmlFor="sheet-month"><DatePicker id="sheet-month" mode="month" value={month} onChange={setMonth} /></Field>
        )}
        <button type="button" onClick={() => setCustom((value) => !value)} className="mt-2 text-caption text-muted-foreground underline-offset-2 hover:text-foreground hover:underline">
          {custom ? 'Use a whole month' : 'Use other dates'}
        </button>
        <p className="mt-4 text-caption text-muted-foreground">
          Every player's unpaid hours logged up to the last day are gathered into it. Hours already on another sheet stay there.
        </p>
        {create.error ? <p className="mt-3 text-caption text-danger">{describeError(create.error)}</p> : null}
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" size="sm" busy={create.isPending} disabled={!ready}>Create sheet</Button>
        </div>
      </form>
    </Dialog>
  )
}
